import { forwardRef } from 'react';

import { Text, theme } from '@prose-ui';
import { styled } from '@prose-ui/legacy';
import { mediaMinWidth } from '@prose-ui/utils/media';

import { TwoUp } from 'Components/TwoUp';

import { introductionContent } from 'assets/content/landingPages/skincare';

import useResponsiveVariant from 'utils/useResponsiveVariant';

import SkincareConsultationCTA from './SkincareConsultationCTA';

const borderStyle = `1px solid ${theme.colors.neutral[900]};`;

const HeadlineBlock = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  padding: 65px 30px;
  gap: 30px;
  background-color: ${theme.colors.neutral[200]};

  ${mediaMinWidth('sm')} {
    padding-left: 56px;
  }
`;

const ImageBlock = styled.div`
  width: 100%;
  border-top: ${borderStyle};

  ${mediaMinWidth('sm')} {
    border-top: none;
    border-right: ${borderStyle};
  }
`;

const SoloImage = styled.img`
  object-fit: cover;
  width: 100%;
  height: 100%;
`;

const StyledSkincareConsultationCTA = styled(SkincareConsultationCTA)`
  width: 69%;
`;

export const IntroductionSection = forwardRef<HTMLDivElement>((_, ref) => {
  const { isMobile } = useResponsiveVariant({ legacy: false });

  const { headline, fallbackImage } = introductionContent;

  const fallbackImgSrc = isMobile ? fallbackImage.src.mobile.src : fallbackImage.src.desktop.src;

  return (
    <TwoUp
      borders={{ top: true, sides: true }}
      dataTestId="skincare-lp-hero"
      desktopOrder="21"
      minHeight={{ mobile: 'auto', desktop: 'calc(100vh - 95px)' }}
      spaceDistribution={{ mobile: 'contents', desktop: 'two-thirds' }}
    >
      <HeadlineBlock ref={ref}>
        <Text as="h1" variant={isMobile ? 'heading5' : 'heading4'}>
          {headline}
        </Text>
        <StyledSkincareConsultationCTA data-click="consultation" data-from="hero" noMargin />
      </HeadlineBlock>
      <ImageBlock>
        <SoloImage alt={fallbackImage.alt} src={fallbackImgSrc} />
      </ImageBlock>
    </TwoUp>
  );
});

IntroductionSection.displayName = 'IntroductionSection';
